import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import React from 'react';
// import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Router, IndexRoute, Route, browserHistory } from 'react-router';
// Redux
import { applyMiddleware, createStore, combineReducers, compose } from 'redux';
import { Provider } from 'react-redux';
import { routerMiddleware, syncHistoryWithStore, routerReducer as routing } from 'react-router-redux';
import { reducer as form } from 'redux-form';
// v1 Route Components
import ApiClient from '../../src/web/helpers/ApiClient';
import Home from '../../src/admin/modules/home/containers/AdminHome';
import PageBuilder from '../../src/admin/modules/pageBuilder/containers/AdminPageBuilder';
import Settings from '../../src/admin/modules/settings/containers/AdminSettings';
import SettingsLandingPage from '../../src/admin/modules/settings/components/LandingPage';
import AnalyticsSettings from '../../src/admin/modules/settings/containers/AnalyticsSettings';
import CertSettings from '../../src/admin/modules/settings/containers/CertSettings';
import HostnameSettings from '../../src/admin/modules/settings/containers/HostnameSettings';
import PageSettings from '../../src/admin/modules/settings/containers/PageSettings';
import RedirectSettings from '../../src/admin/modules/settings/containers/RedirectSettings';
import Blog from '../../src/admin/modules/blog/containers/AdminBlog';
import BlogPostCreate from '../containers/BlogPostCreate';
import BlogPostEdit from '../containers/BlogPostEdit';
import BlogList from '../../src/admin/modules/blog/containers/List';
import Hyper from '../../src/admin/modules/hyper/containers/Hyper';
import HyperDashboard from '../../src/admin/modules/hyper/containers/Dashboard';
import HyperWebsites from '../../src/admin/modules/hyper/containers/Websites';
import HyperEvents from '../../src/admin/modules/hyper/containers/Events';
import Integrations from '../containers/Integrations';
import MediaGallery from '../screens/MediaGallery';
// v1 Helpers
import { versionResolver } from '../../src/web/utils/pageLoader';
import { RedirectableError } from '../../src/shared/errors';
// Reducers and Actions
import createMiddleware from '../../src/web/store/middleware/clientMiddleware';
import { load as loadApp } from '../../src/web/store/modules/app';
import assets from '../../src/web/store/modules/assets';
import builder from '../../src/web/store/modules/builder';
import certificates, { load as loadCertificates } from '../../src/web/store/modules/certificates';
import clientMiddleware from '../../src/web/store/modules/clientMiddleware';
import company from '../../src/web/store/modules/company';
import contact from '../../src/web/store/modules/contact';
import coupon from '../../src/web/store/modules/coupon';
import events, { loadList as loadEvents } from '../../src/web/store/modules/events';
import facilities from '../../src/web/store/modules/facilities';
import files from '../../src/web/store/modules/files';
import googleReviews from '../../src/web/store/modules/googleReviews';
import hostnames, { load as loadHostnames } from '../../src/web/store/modules/hostnames';
import hyper, { loadStats, loadSites } from '../../src/web/store/modules/hyper';
import lead from '../../src/web/store/modules/lead';
import page from '../../src/web/store/modules/page';
import pageBuilder, { load as loadPage, getCurrentVersion, getLayoutVersion } from '../../src/web/store/modules/pageBuilder';
import pages, { searchPages } from '../../src/web/store/modules/pages';
import pageSettings, { loadPaths, getPathsLoaded } from '../../src/web/store/modules/pageSettings';
import reservations from '../../src/web/store/modules/reservations';
import reviews from '../../src/web/store/modules/reviews';
import routes from '../../src/web/store/modules/routes';
import site, { isLoaded as isSiteLoaded } from '../../src/web/store/modules/site';
import storageCalculator from '../../src/web/store/modules/storageCalculator';
import user, { load as loadUser, isAuthenticated as isUserAuthenticated } from '../../src/web/store/modules/user';
import mediaGallery from '../../src/web/store/modules/mediaGallery';
// v2 Components
import ApolloClientProvider from '../../modules/react/components/stateful/ApolloClientProvider';
import App from '../containers/App';
import HostnameCertSettingsEdit from '../screens/HostnameCertSettingsEdit';
import NavigationEdit from '../screens/NavigationEdit';
import PageLayoutEdit from '../screens/PageLayoutEdit';
import RobotsSettingsEdit from '../screens/RobotsSettingsEdit';
import StoreContext from '../../modules/react/components/stateful/StoreProvide';

const { BUGSNAG_API_KEY_FE, BUGSNAG_ENVIRONMENT } = process.env;

Bugsnag.start({
  apiKey: BUGSNAG_API_KEY_FE,
  plugins: [new BugsnagPluginReact()],
  appType: 'admin_frontend',
  releaseStage: BUGSNAG_ENVIRONMENT,
});
BugsnagPerformance.start({ apiKey: BUGSNAG_API_KEY_FE });
const BugsnagErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const reducer = combineReducers({
  assets,
  builder,
  certificates,
  clientMiddleware,
  company,
  contact,
  coupon,
  events,
  facilities,
  files,
  form,
  googleReviews,
  hostnames,
  hyper,
  lead,
  page,
  pageBuilder,
  pages,
  pageSettings,
  reservations,
  reviews,
  routes,
  routing,
  site,
  storageCalculator,
  user,
  mediaGallery,
});

const client = new ApiClient();
const apiClientMiddleware = createMiddleware(client);

const reduxRouterMiddleware = routerMiddleware(browserHistory);

// eslint-disable-next-line no-underscore-dangle
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = createStore(reducer, {}, composeEnhancers(
  applyMiddleware(apiClientMiddleware, reduxRouterMiddleware),
));

const enhancedHistory = syncHistoryWithStore(browserHistory, store);

const onAdminEnter = async (nextState, replace, callback) => {
  if (isUserAuthenticated(store.getState())) {
    return callback();
  }

  try {
    await store.dispatch(loadUser());

    if (isUserAuthenticated(store.getState())) {
      if (!isSiteLoaded(store.getState())) {
        await store.dispatch(loadApp());
      }

      return callback();
    }
  } catch (err) {
    if (err.status !== 401) {
      return callback(err);
    }
  }

  const redirectPath = `/api/auth/login?redirect=${nextState.location.pathname}`;
  window.location = redirectPath;

  return callback();
};

const onHomeEnter = async (nextState, replace, callback) => {
  await store.dispatch(searchPages({}, { perPage: 10, pageNumber: 1 }));

  return callback();
};

const onPageBuilderEnter = async (nextState, replace, callback) => {
  // Always load the page
  await store.dispatch(loadPage(nextState.params.id));

  // Grab the version and load its sections
  const state = store.getState();
  const version = getCurrentVersion(state);
  const layoutVersion = getLayoutVersion(state);
  if (!version) {
    // Redirect to /admin home
    throw new RedirectableError('Page does not have a version', '/admin');
  }

  const promises = [versionResolver(store, version)];
  if (layoutVersion) {
    promises.push(versionResolver(store, layoutVersion));
  }

  await Promise.all(promises);

  return callback();
};

const onPageSettingsEnter = async (nextState, replace, callback) => {
  const promises = [];
  const state = store.getState();

  if (!getPathsLoaded(state)) {
    promises.push(store.dispatch(loadPaths()));
  }

  await Promise.all(promises);

  return callback();
};

const onHostnameSettingsEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadHostnames());

  return callback();
};

const onRedirectSettingsEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadHostnames());

  return callback();
};

const onCertSettingsEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadCertificates());
  await store.dispatch(loadHostnames());

  return callback();
};

const onHyperDashboardEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadStats());

  return callback();
};

const onHyperWebsitesEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadSites());

  return callback();
};

const onHyperEventsEnter = async (nextState, replace, callback) => {
  await store.dispatch(loadEvents());

  return callback();
};

// eslint-disable-next-line arrow-body-style
const onMediaGalleryEnter = async (nextState, replace, callback) => {
  // await store.dispatch(loadMedia());

  return callback();
};

const rootContainer = document.getElementById('root');
const root = createRoot(rootContainer);

root.render(
  <BugsnagErrorBoundary>
    <ApolloClientProvider>
      <Provider store={store}>
        <StoreContext.Provider value={store}>
          <Router history={enhancedHistory}>
            <Route path="admin" component={App} onEnter={onAdminEnter}>
              <IndexRoute component={Home} onEnter={onHomeEnter} />
              <Route path="page/edit/:id" component={PageBuilder} onEnter={onPageBuilderEnter} />
              <Route path="settings" components={Settings}>
                <IndexRoute component={SettingsLandingPage} />
                <Route path="pages" component={PageSettings} onEnter={onPageSettingsEnter} />
                <Route path="analytics" component={AnalyticsSettings} />
                <Route path="domains" component={HostnameSettings} onEnter={onHostnameSettingsEnter} />
                <Route path="domains-ssl" component={HostnameCertSettingsEdit} />
                <Route path="redirects" component={RedirectSettings} onEnter={onRedirectSettingsEnter} />
                <Route path="ssl" component={CertSettings} onEnter={onCertSettingsEnter} />
                <Route path="robots" component={RobotsSettingsEdit} />
              </Route>
              <Route path="blog" component={Blog}>
                <IndexRoute component={BlogList} />
                <Route path="new" component={BlogPostCreate} />
                <Route path="edit/:id" component={BlogPostEdit} />
              </Route>
              <Route path="media-gallery" component={MediaGallery} onEnter={onMediaGalleryEnter}>
                {/* These seemed like helpful guides (based on Blog above) for what to do next */}
                {/* <IndexRoute component={MediaList} onEnter={onMediaListEnter} />
              <Route path="new" component={MediaCreate} onEnter={onMediaCreateEnter} />
              <Route path="edit/:id" component={MediaEdit} onEnter={onMediaEditEnter} /> */}
              </Route>
              <Route path="hyper" component={Hyper}>
                <IndexRoute component={HyperDashboard} onEnter={onHyperDashboardEnter} />
                <Route path="websites" component={HyperWebsites} onEnter={onHyperWebsitesEnter} />
                <Route path="papertrail" component={HyperEvents} onEnter={onHyperEventsEnter} />
                <Route path="integrations" component={Integrations} />
              </Route>
              <Route path="locales/:localeCode/types/:typeId/page-layout/versions/:versionId/edit" component={PageLayoutEdit} />
              <Route path="locales/:code/navigation/edit" component={NavigationEdit} />
            </Route>
          </Router>
        </StoreContext.Provider>
      </Provider>
    </ApolloClientProvider>
  </BugsnagErrorBoundary>,
  document.getElementById('root'),
);
